import type { BodyHandle } from 'box3d-web';
import type { Vec3 } from '../../services/fracture/voronoiFracture';

import {
  advanceDemolitionHostedFixedStep,
  spawnProjectile,
  probeImpact,
  placeCharge,
  probeDetonate,
  setDemoTool,
  setWeaponPower,
  createHostedDemolitionRuntime,
  disposeDemolitionStructure,
  drainDemolitionEvents,
  getDemolitionSnapshot,
  resetDemolition,
  finalizeDemolitionHostedFrame,
  DEMO_FIXED_DT,
  DEMO_SUBSTEPS,
  resetDemolitionClock,
  type DemoTool,
  type DemolitionNativeOwnership,
  type DemolitionNativeParticipant,
  type DemolitionRuntime,
} from '../../structures/demolition/demolitionPhysics';
import type { CanonicalDemolitionEventUnion } from '../../structures/demolition/demolitionEventRing';
import type { ProfileId } from '../../services/destructionProfiles';
import {
  createDestructionIdentityFactory,
  type ElementIdentity,
  type LayerIdentity,
  type StructureIdentity,
} from '../identities';
import {
  SharedDestructionWorld,
  type DestructionStructureHost,
  type StructureHostSnapshot,
  type WorldActorRuntime,
} from '../sharedDestructionWorld';
import type { SceneCommandEnvelope, SceneContactBatch, DestructionSceneParticipant } from '../destructionSceneParticipant';
import type { DestructionSceneCommandPayload } from '../destructionSceneRuntime';

export type DemolitionContactBatch = SceneContactBatch;

const TOOLS: readonly DemoTool[] = ['cannon', 'bomb', 'spike', 'rubble', 'concussion'];

function finiteVector(value: { x: number; y: number; z: number }, name: string, nonZero = false): void {
  if (!value || ![value.x, value.y, value.z].every(Number.isFinite) || (nonZero && Math.hypot(value.x, value.y, value.z) <= 1e-8)) {
    throw new RangeError(`${name} must contain finite ${nonZero ? 'non-zero ' : ''}coordinates`);
  }
}

export interface DemolitionStructureAdapterOptions {
  readonly shared: SharedDestructionWorld;
  readonly structureId: StructureIdentity;
  readonly elementId: ElementIdentity;
  readonly layerId: LayerIdentity;
  readonly materialId?: string;
  readonly profileId?: ProfileId | string;
  /** Existing shared ground body; omitted to create/reuse shared ground:0. */
  readonly groundBody?: BodyHandle;
  /** World translation; rotation/local-frame support is intentionally deferred. */
  readonly worldOffset?: Vec3;
}

export interface DemolitionStructureSnapshot {
  readonly structure: StructureHostSnapshot;
  readonly runtime: ReturnType<typeof getDemolitionSnapshot>;
}

export interface DemolitionStructureAdapter {
  readonly structureKey: string;
  readonly revision: number;
  readonly runtime: DemolitionRuntime;
  reset(): void;
  drainEvents(): readonly CanonicalDemolitionEventUnion[];
  snapshot(): DemolitionStructureSnapshot;
  dispose(): void;
}

export interface HostedDemolitionSceneParticipant extends DemolitionStructureAdapter, DestructionSceneParticipant<CanonicalDemolitionEventUnion> {
  readonly physicsWorld: DemolitionRuntime['world'];
  readonly fixedDt: number;
  readonly substeps: number;
  attachSceneOwner(owner: object): void;
  detachSceneOwner(owner: object): void;
  sceneFixedStep(owner: object, batch: DemolitionContactBatch): void;
  finishSceneFrame(owner: object, delta: number, stepped: boolean): void;
  drainSceneEvents(owner: object): readonly CanonicalDemolitionEventUnion[];
  sceneReset(owner: object): void;
  sceneResetClock(owner: object): void;
  ownsProjectileBody(body: BodyHandle): boolean;
}

/** Embeds Demolition as one independently resettable shared-scene participant. */
class HostedDemolitionStructureAdapter implements HostedDemolitionSceneParticipant {
  private disposed = false;
  private sceneOwner: object | null = null;
  readonly runtime: DemolitionRuntime;
  private readonly host: DestructionStructureHost;

  private constructor(
    runtime: DemolitionRuntime,
    host: DestructionStructureHost,
  ) {
    this.runtime = runtime;
    this.host = host;
  }

  get structureKey(): string { return this.host.structureId.key; }
  get revision(): number { return this.host.revision; }
  get physicsWorld(): DemolitionRuntime['world'] { return this.runtime.world; }
  get fixedDt(): number { return DEMO_FIXED_DT; }
  get substeps(): number { return DEMO_SUBSTEPS; }

  static async create(options: DemolitionStructureAdapterOptions): Promise<HostedDemolitionSceneParticipant> {
    const { shared, structureId } = options;
    const host = shared.createStructureHost(structureId);
    try {
      const ground = options.groundBody === undefined
        ? shared.ensureGround()
        : findWorldActorByBody(shared, options.groundBody);
      if (ground === undefined) throw new Error('supplied ground body is not registered in the shared world');
      const ids = createDestructionIdentityFactory({
        namespace: structureId.namespace,
        generation: structureId.generation,
      });
      const projectileBodies = new Set<number>();
      const ownership: DemolitionNativeOwnership = {
        registerBody: (body, participant) => {
          if (participant.kind === 'projectile') {
            const ordinal = shared.allocateWorldActorOrdinal('projectile');
            shared.registerWorldBody(body, {
              scope: 'world',
              worldId: shared.worldId,
              actor: 'projectile',
              ordinal,
              ...(options.materialId === undefined ? {} : { materialId: options.materialId }),
            });
            projectileBodies.add(body as number);
          } else {
            host.registerBody(body, addressForParticipant(options, ids, participant));
          }
        },
        registerShape: (shape, body, participant) => {
          if (participant.kind === 'projectile') shared.registerWorldShape(shape, body);
          else host.registerShape(shape, body);
        },
        unregisterBody: (body) => {
          if (projectileBodies.delete(body as number)) shared.unregisterWorldBody(body);
          else host.unregisterBody(body);
        },
      };
      const runtime = await createHostedDemolitionRuntime(options.profileId ?? 'concrete', {
        world: shared.world,
        groundBody: ground.body,
        ownership,
        worldOffset: options.worldOffset,
      });
      return new HostedDemolitionStructureAdapter(runtime, host);
    } catch (error) {
      host.dispose();
      throw error;
    }
  }

  reset(): void {
    this.assertLive();
    this.assertDetached();
    resetDemolition(this.runtime);
    this.host.advanceRevision();
  }

  drainEvents(): readonly CanonicalDemolitionEventUnion[] {
    this.assertLive();
    this.assertDetached();
    return drainDemolitionEvents(this.runtime);
  }

  prepareSceneCommand(type: string, payload: unknown): unknown {
    switch (type) {
      case 'fire': {
        const p = payload as DestructionSceneCommandPayload['fire'];
        finiteVector(p.target, 'fire.target'); finiteVector(p.direction, 'fire.direction', true);
        if (!(TOOLS as readonly string[]).includes(p.tool) || !Number.isFinite(p.power) || p.power <= 0) throw new RangeError('fire tool and power are invalid');
        return Object.freeze({ tool: p.tool, power: p.power, target: Object.freeze({ ...p.target }), direction: Object.freeze({ ...p.direction }) });
      }
      case 'probe-impact': {
        const p = payload as DestructionSceneCommandPayload['probe-impact'];
        finiteVector(p, 'probe-impact.point');
        if (p.speed !== undefined && (!Number.isFinite(p.speed) || p.speed < 0)) throw new RangeError('probe-impact.speed must be finite and non-negative');
        if (p.direction !== undefined) finiteVector(p.direction, 'probe-impact.direction', true);
        return Object.freeze({ x: p.x, y: p.y, z: p.z, ...(p.speed === undefined ? {} : { speed: p.speed }), ...(p.direction === undefined ? {} : { direction: Object.freeze({ ...p.direction }) }) });
      }
      case 'place-charge': {
        const p = payload as DestructionSceneCommandPayload['place-charge'];
        finiteVector(p, 'place-charge.point');
        if (p.power !== undefined && (!Number.isFinite(p.power) || p.power <= 0)) throw new RangeError('place-charge.power must be finite and positive');
        return Object.freeze({ ...p });
      }
      case 'set-weapon-power': {
        const p = payload as DestructionSceneCommandPayload['set-weapon-power'];
        if (!Number.isFinite(p.power) || p.power <= 0) throw new RangeError('weapon power must be finite and positive');
        return Object.freeze({ power: p.power });
      }
      case 'set-tool': {
        const p = payload as DestructionSceneCommandPayload['set-tool'];
        if (!(TOOLS as readonly string[]).includes(p.tool)) throw new RangeError('tool is invalid');
        return Object.freeze({ tool: p.tool });
      }
      case 'detonate': {
        const p = payload as DestructionSceneCommandPayload['detonate'];
        finiteVector(p, 'detonate.point');
        if (!Number.isFinite(p.power) || p.power <= 0) throw new RangeError('detonate.power must be finite and positive');
        return Object.freeze({ ...p });
      }
      case 'set-material': {
        throw new Error('set-material is not supported by hosted demolition yet');
      }
      case 'reset-structure': case 'dispose-structure': return Object.freeze({});
      default: return payload;
    }
  }

  sceneCommandPolicy(type: string): 'critical' | 'coalescible' {
    return type === 'set-tool' || type === 'set-weapon-power' ? 'coalescible' : 'critical';
  }

  applySceneCommand(owner: object, command: SceneCommandEnvelope): void {
    this.assertLive();
    this.assertOwner(owner);
    switch (command.type) {
      case 'fire': {
        const p = command.payload as DestructionSceneCommandPayload['fire'];
        const length = Math.hypot(p.direction.x, p.direction.y, p.direction.z);
        const direction = { x: p.direction.x / length, y: p.direction.y / length, z: p.direction.z / length };
        spawnProjectile(this.runtime, {
          x: p.target.x - direction.x * 8,
          y: p.target.y - direction.y * 8,
          z: p.target.z - direction.z * 8,
        }, direction, p.tool as DemoTool, p.power);
        break;
      }
      case 'probe-impact': { const p = command.payload as DestructionSceneCommandPayload['probe-impact']; probeImpact(this.runtime, p.x, p.y, p.z, p.speed, p.direction); break; }
      case 'place-charge': { const p = command.payload as DestructionSceneCommandPayload['place-charge']; placeCharge(this.runtime, p.x, p.y, p.z, p.power); break; }
      case 'set-weapon-power': { const p = command.payload as DestructionSceneCommandPayload['set-weapon-power']; setWeaponPower(this.runtime, p.power); break; }
      case 'set-tool': { const p = command.payload as DestructionSceneCommandPayload['set-tool']; setDemoTool(this.runtime, p.tool as DemoTool); break; }
      case 'detonate': { const p = command.payload as DestructionSceneCommandPayload['detonate']; probeDetonate(this.runtime, p.x, p.y, p.z, p.power); break; }
      case 'set-material': throw new Error('set-material is not supported by hosted demolition yet');
      default: throw new Error(`unsupported demolition command ${command.type}`);
    }
  }

  attachSceneOwner(owner: object): void {
    this.assertLive();
    if (this.sceneOwner !== null && this.sceneOwner !== owner) throw new Error('demolition adapter is already attached to a scene owner');
    this.sceneOwner = owner;
  }

  detachSceneOwner(owner: object): void {
    if (this.sceneOwner === owner) this.sceneOwner = null;
  }

  sceneFixedStep(owner: object, batch: DemolitionContactBatch): void {
    this.assertLive();
    this.assertOwner(owner);
    advanceDemolitionHostedFixedStep(this.runtime, batch);
  }

  finishSceneFrame(owner: object, delta: number, stepped: boolean): void {
    this.assertLive();
    this.assertOwner(owner);
    finalizeDemolitionHostedFrame(this.runtime, delta, stepped);
  }

  drainSceneEvents(owner: object): readonly CanonicalDemolitionEventUnion[] {
    this.assertLive();
    this.assertOwner(owner);
    return drainDemolitionEvents(this.runtime);
  }

  sceneReset(owner: object): void {
    this.assertLive();
    this.assertOwner(owner);
    resetDemolition(this.runtime);
    this.host.advanceRevision();
  }

  sceneResetClock(owner: object): void {
    this.assertLive();
    this.assertOwner(owner);
    resetDemolitionClock(this.runtime);
  }

  ownsProjectileBody(body: BodyHandle): boolean {
    return !this.disposed && this.runtime.cannonballSet.has(body as number);
  }

  snapshot(): DemolitionStructureSnapshot {
    this.assertLive();
    const structure = this.host.snapshot();
    return Object.freeze({
      structure,
      runtime: getDemolitionSnapshot(this.runtime),
    });
  }

  dispose(): void {
    if (this.disposed) return;
    this.assertDetached();
    try {
      disposeDemolitionStructure(this.runtime);
    } finally {
      try {
        this.host.dispose();
      } finally {
        this.disposed = true;
      }
    }
  }

  private assertLive(): void {
    if (this.disposed) throw new Error('demolition structure adapter used after dispose()');
  }

  private assertDetached(): void {
    if (this.sceneOwner !== null) throw new Error('demolition adapter is scene-owned; use DestructionSceneRuntime');
  }

  private assertOwner(owner: object): void {
    if (this.sceneOwner !== owner) throw new Error('demolition adapter is not attached to this scene owner');
  }
}

function findWorldActorByBody(shared: SharedDestructionWorld, body: BodyHandle): WorldActorRuntime | undefined {
  return shared.snapshot().worldActors
    .map((actor) => actor.body === body && actor.address.scope === 'world'
      ? shared.getWorldActor(actor.address.actor, actor.address.ordinal)
      : undefined)
    .find((actor): actor is WorldActorRuntime => actor !== undefined);
}

function addressForParticipant(
  options: DemolitionStructureAdapterOptions,
  ids: ReturnType<typeof createDestructionIdentityFactory>,
  participant: DemolitionNativeParticipant,
) {
  if (participant.kind === 'projectile') {
    throw new Error('projectile participants must be registered as shared-world actors');
  }
  if (participant.kind === 'ground') {
    throw new Error('hosted demolition cannot own the shared ground');
  }
  const cellId = participant.kind === 'cell' ? ids.cell(participant.cellId, options.layerId) : undefined;
  return {
    scope: 'structure' as const,
    worldId: options.shared.worldId,
    structureId: options.structureId,
    elementId: options.elementId,
    layerId: options.layerId,
    ...(cellId === undefined ? {} : { cellId }),
    ...(options.materialId === undefined ? {} : { materialId: options.materialId }),
  };
}

export async function createDemolitionStructureAdapter(
  options: DemolitionStructureAdapterOptions,
): Promise<HostedDemolitionSceneParticipant> {
  return HostedDemolitionStructureAdapter.create(options);
}
